<script setup>
import { i18n } from 'boot/i18n';
import { HrCheckbox } from 'src/design-system';
import { formatCalendarDate } from 'src/utils/DateUtils';
import { computed } from 'vue';

/**
 * The last look at a code before it exists — every value restated, and the affirmation that gates it.
 *
 * A SEPARATE STEP, NOT A SUMMARY BLOCK UNDER THE FORM, and the difference is what it asks of the
 * reader. Everything intrinsic to a code is immutable once written: the string, the unit basis and
 * the start date can never be edited, and a clinic may hold only one record per code string. So the
 * moment before the write is the last moment any of it can be corrected, and a step that shows
 * nothing but those four values is the only arrangement that makes reading them the task rather
 * than something to scroll past on the way to a button.
 *
 * IT RESTATES RATHER THAN RE-EDITS. There are no fields here — going back is how a value changes,
 * which keeps one source for each of them and makes "what am I confirming" unambiguous.
 *
 * THE DATE IS FORMATTED FOR READING, through the app-wide `formatCalendarDate`. A confirmation
 * showing `2026-01-01` asks somebody to parse a storage format at exactly the moment they are being
 * asked to vouch for it.
 */
defineOptions({ name: 'MintServiceCodeReview' });

const props = defineProps({
  /** The completed draft: code, descriptor, unitBasis, validFrom. */
  draft: {
    type: Object,
    required: true,
  },
  /** Whether the affirmation has been given. Supports `v-model:confirmed`. */
  confirmed: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits({
  /** Fired when the affirmation is toggled. */
  'update:confirmed': null,
});

const KEY = 'settings.billing.chargeMaster.mint';

/**
 * The four values as rows, in the order they were entered.
 *
 * SAME ORDER AS THE FORM, deliberately — which is why the date sits second rather than last. A
 * review that reshuffles what it is reviewing makes the reader match up two lists instead of
 * reading one, and the form pairs the two short fields on one row.
 */
const rows = computed(() => [
  { key: 'code', label: i18n.t(`${KEY}.inputCode`), value: props.draft.code },
  {
    key: 'validFrom',
    label: i18n.t(`${KEY}.inputValidFrom`),
    value: formatCalendarDate(props.draft.validFrom),
  },
  { key: 'descriptor', label: i18n.t(`${KEY}.inputDescriptor`), value: props.draft.descriptor },
  {
    key: 'unitBasis',
    label: i18n.t(`${KEY}.inputUnitBasis`),
    value: i18n.t(`unitBases.${props.draft.unitBasis}`),
  },
]);
</script>

<template>
  <div class="row q-col-gutter-md">
    <p class="col-12 q-mb-none hr-text-on-surface-variant">
      {{ i18n.t(`${KEY}.textReviewLede`) }}
    </p>

    <!--
      A DESCRIPTION LIST, because that is what this is: four labeled values. It gives a screen
      reader the label/value pairing for free, where a grid of divs would read as eight loose strings
      in an order nobody stated.

      LAID OUT ON THE APP'S COLUMN GRID rather than a bespoke one — each pair is a row of two columns
      on a tablet and up, and stacks below that, which is what `col-sm-4` / `col-sm-8` already means.
    -->
    <dl class="col-12 row q-col-gutter-sm q-my-none">
      <template
        v-for="row in rows"
        :key="row.key"
      >
        <dt class="col-12 col-sm-4 hr-body-small hr-text-on-surface-variant">
          {{ row.label }}
        </dt>
        <!--
          `q-ma-none` and not `q-mb-none`: a `dd` carries a browser default `margin-inline-start` of
          40px, which pushed a `col-sm-8` past the width left in the row and wrapped every value onto
          its own indented line. Clearing the bottom margin alone left the one that mattered.
        -->
        <dd
          class="col-12 col-sm-8 q-ma-none"
          :data-e2e="`text-review-${row.key}`"
        >
          {{ row.value }}
        </dd>
      </template>
    </dl>

    <!--
      THE GATE. It names the three things that cannot be corrected afterwards — the code, its basis
      and its dates — because a wrong basis is a fourfold error on every claim the code produces and
      there is no edit path to undo it.
    -->
    <hr-checkbox
      :model-value="props.confirmed"
      class="col-12"
      :label="i18n.t(`${KEY}.checkboxConfirm`)"
      data-e2e="checkbox-mint-confirm"
      @update:model-value="emit('update:confirmed', $event)"
    />
  </div>
</template>
